import { useState, useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";

// Components
import SplashScreen from "@/components/SplashScreen";

// Pages
import LoginPage from "@/pages/LoginPage";
import AdminLoginPage from "@/pages/AdminLoginPage";
import AdminSettingsPage from "@/pages/AdminSettingsPage";
import PhilosopherLibraryPage from "@/pages/PhilosopherLibraryPage";
import StudentDashboard from "@/pages/StudentDashboard";
import DirectorDashboard from "@/pages/DirectorDashboard";
import MultiSchoolAdminPage from "@/pages/MultiSchoolAdminPage";
import DebatePage from "@/pages/DebatePage";
import QuizPage from "@/pages/QuizPage";
import DemoRequestPage from "@/pages/DemoRequestPage";
import MentionsLegalesPage from "@/pages/MentionsLegalesPage";
import AboutPage from "@/pages/AboutPage";
import DiagnosticPage from "@/pages/DiagnosticPage";
import DissertationModule from "@/pages/DissertationModule";
import CommentaireModule from "@/pages/CommentaireModule";
import ChatPage from "@/pages/ChatPage";
import GroupChatPage from "@/pages/GroupChatPage";
import LeaderboardPage from "@/pages/LeaderboardPage";
import ModerationPage from "@/pages/ModerationPage";
import StudentTrackingPage from "@/pages/StudentTrackingPage";
import ClassManagementPage from "@/pages/ClassManagementPage";
import JoinClassPage from "@/pages/JoinClassPage";
import ThematiquesPage from "@/pages/ThematiquesPage";
import UserManualPage from "@/pages/UserManualPage";
import AdminManualPage from "@/pages/AdminManualPage";
import ClassQRCodePage from "@/pages/ClassQRCodePage";
import ChatObservationPage from "@/pages/ChatObservationPage";

function App() {
  const [user, setUser] = useState(null);
  const [userType, setUserType] = useState(null);
  const [showSplash, setShowSplash] = useState(true);

  useEffect(() => {
    const savedUser = localStorage.getItem("philoprof_user");
    const savedType = localStorage.getItem("philoprof_type");
    if (savedUser && savedType) {
      setUser(JSON.parse(savedUser));
      setUserType(savedType);
    }
  }, []);

  const handleLogin = (userData, type) => {
    setUser(userData);
    setUserType(type);
    localStorage.setItem("philoprof_user", JSON.stringify(userData));
    localStorage.setItem("philoprof_type", type);
  };

  const handleLogout = () => {
    setUser(null);
    setUserType(null);
    localStorage.removeItem("philoprof_user");
    localStorage.removeItem("philoprof_type");
  };

  return (
    <div className="app-container">
      {showSplash && <SplashScreen onComplete={() => setShowSplash(false)} />}
      <Toaster position="top-right" richColors />
      <BrowserRouter>
        <Routes>
          <Route 
            path="/" 
            element={
              user ? (
                userType === "director" || userType === "admin" ? 
                  <Navigate to="/director" replace /> : 
                  <Navigate to="/dashboard" replace />
              ) : (
                <LoginPage onLogin={handleLogin} />
              )
            } 
          />
          <Route 
            path="/admin" 
            element={
              user && (userType === "admin" || userType === "director") ? (
                <Navigate to="/director" replace />
              ) : (
                <AdminLoginPage onLogin={handleLogin} />
              )
            } 
          />
          <Route 
            path="/demo" 
            element={<DemoRequestPage />} 
          />
          <Route 
            path="/legal" 
            element={<MentionsLegalesPage />} 
          />
          <Route 
            path="/about" 
            element={<AboutPage />} 
          />
          <Route 
            path="/dashboard" 
            element={
              user && userType === "student" ? (
                <StudentDashboard user={user} onLogout={handleLogout} />
              ) : (
                <Navigate to="/" replace />
              )
            } 
          />
          <Route 
            path="/director" 
            element={
              user && (userType === "director" || userType === "admin") ? (
                <DirectorDashboard user={user} onLogout={handleLogout} />
              ) : (
                <Navigate to="/" replace />
              )
            } 
          />
          <Route 
            path="/moderation" 
            element={
              user && (userType === "director" || userType === "admin") ? (
                <ModerationPage user={user} />
              ) : (
                <Navigate to="/" replace />
              )
            } 
          />
          <Route 
            path="/tracking" 
            element={
              user && (userType === "director" || userType === "admin") ? (
                <StudentTrackingPage user={user} />
              ) : (
                <Navigate to="/" replace />
              )
            } 
          />
          <Route 
            path="/observation" 
            element={
              user && (userType === "director" || userType === "admin") ? (
                <ChatObservationPage user={user} />
              ) : (
                <Navigate to="/" replace />
              )
            } 
          />
          <Route 
            path="/diagnostic" 
            element={
              user && userType === "student" ? (
                <DiagnosticPage user={user} setUser={setUser} />
              ) : (
                <Navigate to="/" replace />
              )
            } 
          />
          <Route 
            path="/dissertation" 
            element={
              user && userType === "student" ? (
                <DissertationModule user={user} />
              ) : (
                <Navigate to="/" replace />
              )
            } 
          />
          <Route 
            path="/commentaire" 
            element={
              user && userType === "student" ? (
                <CommentaireModule user={user} />
              ) : (
                <Navigate to="/" replace />
              )
            } 
          />
          <Route 
            path="/chat" 
            element={
              user && userType === "student" ? (
                <ChatPage user={user} />
              ) : (
                <Navigate to="/" replace />
              )
            } 
          />
          <Route 
            path="/chat/groupe" 
            element={
              user && userType === "student" ? (
                <GroupChatPage user={user} />
              ) : (
                <Navigate to="/" replace />
              )
            } 
          />
          <Route 
            path="/debate" 
            element={
              user && userType === "student" ? (
                <DebatePage user={user} />
              ) : (
                <Navigate to="/" replace />
              )
            } 
          />
          <Route 
            path="/quiz" 
            element={
              user && userType === "student" ? (
                <QuizPage user={user} />
              ) : (
                <Navigate to="/" replace />
              )
            } 
          />
          <Route 
            path="/leaderboard" 
            element={
              user ? (
                <LeaderboardPage user={user} userType={userType} />
              ) : (
                <Navigate to="/" replace />
              )
            } 
          />
          <Route 
            path="/classes" 
            element={
              user && (userType === "director" || userType === "admin") ? (
                <ClassManagementPage user={user} />
              ) : (
                <Navigate to="/" replace />
              )
            } 
          />
          <Route 
            path="/join/:classId" 
            element={<JoinClassPage onLogin={handleLogin} />} 
          />
          <Route 
            path="/settings" 
            element={
              user && (userType === "director" || userType === "admin") ? (
                <AdminSettingsPage user={user} />
              ) : (
                <Navigate to="/" replace />
              )
            } 
          />
          <Route 
            path="/admin/schools" 
            element={
              user && (userType === "director" || userType === "admin") ? (
                <MultiSchoolAdminPage user={user} onLogout={handleLogout} />
              ) : (
                <Navigate to="/" replace />
              )
            } 
          />
          <Route 
            path="/library" 
            element={<PhilosopherLibraryPage user={user} />} 
          />
          <Route 
            path="/library/:philosopherId" 
            element={<PhilosopherLibraryPage user={user} />} 
          />
          <Route 
            path="/thematiques" 
            element={
              user && userType === "student" ? (
                <ThematiquesPage user={user} />
              ) : (
                <Navigate to="/" replace />
              )
            } 
          />
          <Route 
            path="/aide" 
            element={
              user && userType === "student" ? (
                <UserManualPage user={user} />
              ) : (
                <Navigate to="/" replace />
              )
            } 
          />
          <Route 
            path="/admin-aide" 
            element={
              user && (userType === "director" || userType === "admin") ? (
                <AdminManualPage user={user} />
              ) : (
                <Navigate to="/" replace />
              )
            } 
          />
          <Route 
            path="/classe-qr/:classId" 
            element={
              user && (userType === "director" || userType === "admin") ? (
                <ClassQRCodePage user={user} />
              ) : (
                <Navigate to="/" replace />
              )
            } 
          />
          <Route 
            path="/rejoindre/:etablissementId/:classId" 
            element={<LoginPage preselectedEtab={true} />} 
          />
        </Routes>
      </BrowserRouter>
    </div>
  );
}

export default App;
